<template>
  <div v-if="show" class="inicializar-overlay" @click.self="close">
    <div class="inicializar-modal" role="dialog" aria-modal="true">
      <button class="inicializar-close" @click="close" aria-label="Fechar">
        <i class="fas fa-times"></i>
      </button>

      <div class="inicializar-header">
        <i class="fas fa-database inicializar-icon"></i>
        <h2>Inicializar índices</h2>
        <p class="inicializar-subtitle">Cria índices ausentes nos bancos MongoDB do cliente</p>
      </div>

      <div class="inicializar-aviso">
        <i class="fas fa-info-circle"></i>
        <span>
          <strong>Somente índices.</strong> Não apaga nem altera dados existentes. Operação idempotente — pode ser executada várias vezes com segurança.
        </span>
      </div>

      <div class="inicializar-form">
        <div class="form-group">
          <label class="form-label"><strong>Cliente</strong></label>
          <base-select
            id="cliente-inicializar"
            inputClasses="form-control"
            :disabled="loading"
            defaultOption="Selecione o cliente..."
            :options="clientesDisponiveis"
            v-model="clienteSelecionado"
            :class="{ 'is-invalid': errors.cliente }"
          />
          <div v-if="errors.cliente" class="invalid-feedback d-block">{{ errors.cliente }}</div>
        </div>

        <div class="form-group mt-3">
          <label class="form-label"><strong>Ambiente</strong></label>
          <select
            id="ambiente-inicializar"
            v-model="ambiente"
            :disabled="loading"
            class="form-control"
            :class="{ 'is-warning': isProducao }"
          >
            <option value="test">Desenvolvimento (teste)</option>
            <option value="prod">Produção</option>
          </select>
          <div v-if="isProducao" class="alert alert-danger mt-2 mb-0">
            <i class="fas fa-exclamation-triangle"></i>
            <strong> Atenção:</strong> Você está selecionando o ambiente de produção!
          </div>
        </div>
      </div>

      <div class="inicializar-actions">
        <base-button type="secondary" :disabled="loading" @click="close">
          Cancelar
        </base-button>
        <base-button
          type="primary"
          :disabled="loading || !canSubmit"
          @click="confirmarInicializacao"
        >
          <i v-if="loading" class="fas fa-spinner fa-spin mr-1"></i>
          <i v-else class="fas fa-play mr-1"></i>
          Inicializar
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ClienteInicializarSistema',
  props: {
    show: { type: Boolean, default: false },
  },
  data() {
    return {
      clienteSelecionado: '',
      ambiente: 'test',
      loading: false,
      errors: {},
    };
  },
  computed: {
    ...mapGetters({
      clientes: 'clientes/getCustomerList',
    }),
    clientesDisponiveis() {
      if (!this.clientes || this.clientes.length === 0) return [];
      return this.clientes
        .filter((c) => c.statusCliente !== 'INATIVO')
        .map((c) => ({ desc: `${c.nome} (${c.identificacao})`, value: c.identificacao }))
        .sort((a, b) => a.desc.localeCompare(b.desc));
    },
    isProducao() {
      return this.ambiente === 'prod';
    },
    canSubmit() {
      return !!this.clienteSelecionado;
    },
  },
  watch: {
    show(val) {
      if (val) {
        this.resetForm();
        if (!this.clientes || this.clientes.length === 0) {
          this.fetchCustomers();
        }
      }
    },
  },
  methods: {
    ...mapActions({
      fetchCustomers: 'clientes/fetchCustomers',
    }),

    resetForm() {
      this.clienteSelecionado = '';
      this.ambiente = 'test';
      this.loading = false;
      this.errors = {};
    },

    close() {
      if (!this.loading) {
        this.$emit('close');
      }
    },

    validar() {
      this.errors = {};
      if (!this.clienteSelecionado) {
        this.errors.cliente = 'Selecione um cliente.';
        return false;
      }
      return true;
    },

    async confirmarInicializacao() {
      if (!this.validar()) return;

      const ambienteLabel = this.isProducao ? 'PRODUÇÃO' : 'DESENVOLVIMENTO (teste)';
      const result = await this.$swal({
        type: 'warning',
        title: 'Confirmar inicialização de índices',
        html: `
          <p>Cliente: <strong>${this.clienteSelecionado}</strong></p>
          <p>Ambiente: <strong>${ambienteLabel}</strong></p>
          <p class="mt-3 text-muted">Esta operação cria índices ausentes. Não apaga nem altera dados existentes.</p>
        `,
        showCancelButton: true,
        confirmButtonText: 'Sim, inicializar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#3085d6',
      });

      if (!result.value) return;

      await this.executarInicializacao();
    },

    async executarInicializacao() {
      this.loading = true;

      try {
        const api = new this.$httpClient();
        const response = await api.send(
          '/sistema/inicializar',
          'POST',
          {
            cliente: this.clienteSelecionado,
            ambiente: this.ambiente,
          },
          undefined,
          false
        );

        if (response.status !== 200) {
          throw new Error(response.data?.mensagem || 'Erro ao inicializar sistema');
        }

        this.$swal({
          type: 'success',
          toast: true,
          timer: 3000,
          showConfirmButton: false,
          title: response.data?.mensagem || 'Inicialização acionada com sucesso!',
          grow: 'fullscreen',
        });

        this.$emit('close');
      } catch (error) {
        this.$swal({
          type: 'error',
          toast: true,
          timer: 4000,
          showConfirmButton: false,
          title: error.message || 'Erro ao inicializar sistema',
          grow: 'fullscreen',
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.inicializar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 0, 40, 0.92);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inicializar-modal {
  background: var(--qs-panel-bg, #fff);
  border-radius: 12px;
  padding: 2rem;
  width: 90%;
  max-width: 520px;
  position: relative;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  animation: fadeIn 0.2s;
}

.inicializar-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: none;
  border: none;
  font-size: 1.2rem;
  color: var(--text-muted, #6c757d);
  cursor: pointer;
  padding: 0.25rem 0.5rem;
}

.inicializar-close:hover {
  color: var(--text-primary, #333);
}

.inicializar-header {
  text-align: center;
  margin-bottom: 1.5rem;
}

.inicializar-icon {
  font-size: 2.5rem;
  color: var(--color-nav-accent, #00a4ff);
  margin-bottom: 0.5rem;
}

.inicializar-header h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
}

.inicializar-subtitle {
  color: var(--text-muted, #6c757d);
  font-size: 0.9rem;
  margin: 0;
}

.inicializar-aviso {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: rgba(0, 124, 215, 0.1);
  border: 1px solid rgba(0, 124, 215, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  font-size: 0.9rem;
  line-height: 1.4;
}

.inicializar-aviso i {
  color: #0078d7;
  margin-top: 2px;
  flex-shrink: 0;
}

.inicializar-form .form-label {
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
}

.is-warning {
  border-color: #dc3545 !important;
}

.inicializar-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
